/**
 * Outcome Governance — Dashboard.
 *
 * Route: /governance/outcomes
 *
 * The continuous-conduct-monitoring surface. Not a regulator, not a
 * decisioning system — it continuously measures, explains and evidences
 * consumer outcomes.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import api from "@/lib/api";
import {
  ShieldCheck, TrendingUp, AlertTriangle, Activity,
  CheckCircle2, HelpCircle, Users, Info,
} from "lucide-react";

const NAVY = "#0A1F44";
const GOLD = "#C9A24A";
const TEAL = "#0D9488";

const STATUS_TONE = {
  good:            { label: "Good",           bg: "#D1FAE5", color: "#065F46" },
  acceptable:      { label: "Acceptable",     bg: "#DBEAFE", color: "#1E40AF" },
  attention:       { label: "Attention",      bg: "#FEF3C7", color: "#92400E" },
  at_risk:         { label: "At risk",        bg: "#FEE2E2", color: "#7F1D1D" },
  harm_detected:   { label: "Harm detected",  bg: "#FEE2E2", color: "#7F1D1D" },
  inconclusive:    { label: "Inconclusive",   bg: "#F1F5F9", color: "#475569" },
};

const RISK_TONE = {
  low:      { label: "Low",      bg: "#D1FAE5", color: "#065F46" },
  medium:   { label: "Medium",   bg: "#FEF3C7", color: "#92400E" },
  high:     { label: "High",     bg: "#FEE2E2", color: "#7F1D1D" },
  critical: { label: "Critical", bg: "#FEE2E2", color: "#7F1D1D" },
};

export default function OutcomeDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/outcome-governance/dashboard");
      setData(data);
    } catch {
      toast.error("Failed to load Outcome Dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-10 text-sm text-slate-500">Loading Outcome Dashboard…</div>;
  if (!data) return null;

  const good = data.good_rate;
  const risk = data.at_risk_rate;

  return (
    <div className="max-w-6xl mx-auto p-6 lg:p-10 space-y-8" data-testid="outcome-dashboard">
      <header>
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>
          Governance · Outcome Governance Engine
        </div>
        <h1 className="font-display text-3xl font-semibold" style={{ color: NAVY }}>Consumer Outcomes</h1>
        <p className="text-[15px] text-slate-600 max-w-3xl mt-2 leading-relaxed">
          Continuous conduct monitoring. Every completed Trust Request evaluates the resulting consumer outcome — fair, explainable, evidence-based. No black-box decisions.
        </p>
      </header>

      {/* Positioning ribbon */}
      <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 flex items-start gap-3">
        <Info className="w-4 h-4 shrink-0 mt-0.5" style={{ color: NAVY }} />
        <div className="text-[12px] text-slate-600 leading-relaxed">
          <strong style={{ color: NAVY }}>What this is.</strong> Continuous conduct monitoring — not point-in-time compliance. The Outcome Engine sits above the Trust Utility and evaluates every completed Trust Request against tenant-configurable rules. Every outcome is explainable and hash-linked to its supporting evidence.
        </div>
      </div>

      {/* Top KPIs */}
      <section data-testid="og-kpis" className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile icon={Activity}      label="Outcomes evaluated"  value={data.total_outcomes} accent={NAVY} />
        <KpiTile icon={CheckCircle2}  label="Good / Acceptable"   value={data.good_or_acceptable} accent="#065F46" />
        <KpiTile icon={AlertTriangle} label="At risk"             value={data.at_risk} accent="#7F1D1D" />
        <KpiTile icon={TrendingUp}    label="Good outcome rate"   value={good != null ? `${Math.round(good * 100)}%` : null} accent={TEAL} emptyHint="No outcomes yet" />
      </section>

      {/* Secondary */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="og-secondary">
        <KpiTile icon={HelpCircle} label="Attention needed" value={data.attention} accent="#92400E" />
        <KpiTile icon={AlertTriangle} label="At-risk rate" value={risk != null ? `${Math.round(risk * 100)}%` : null} accent="#7F1D1D" emptyHint="No outcomes yet" />
        <KpiTile icon={ShieldCheck} label="Avg confidence" value={data.avg_confidence != null ? `${Math.round(data.avg_confidence * 100)}%` : null} accent={NAVY} emptyHint="No outcomes yet" />
        <KpiTile icon={Users} label="Consumer types" value={Object.keys(data.by_type || {}).length} accent={GOLD} />
      </section>

      {/* Outcome trend by type */}
      <section data-testid="og-by-type">
        <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">
          Outcome types
        </div>
        {Object.keys(data.by_type || {}).length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 p-6 text-center text-[13px] text-slate-500">
            No outcomes evaluated yet. Complete a Trust Request to see outcomes appear here.
          </div>
        ) : (
          <div className="space-y-1">
            {Object.entries(data.by_type).map(([type, count]) => (
              <div key={type} className="flex items-center gap-3 rounded-xl bg-white border border-slate-100 p-3">
                <div className="font-semibold text-[13px] capitalize" style={{ color: NAVY }}>
                  {type.replace(/_/g, " ")}
                </div>
                <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-2 rounded-full" style={{
                    background: GOLD,
                    width: `${Math.min(100, (count / Math.max(1, data.total_outcomes)) * 100)}%`,
                  }}/>
                </div>
                <div className="text-[12px] font-mono text-slate-500">{count}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Risk distribution */}
      <section data-testid="og-risk">
        <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">
          Risk distribution
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {["low", "medium", "high", "critical"].map((r) => {
            const tone = RISK_TONE[r];
            const n = data.by_risk?.[r] ?? 0;
            return (
              <div key={r} data-testid={`risk-${r}`} className="rounded-2xl bg-white border border-slate-100 p-3">
                <span className="text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded"
                  style={{ background: tone.bg, color: tone.color }}>{tone.label}</span>
                <div className="font-display text-2xl font-semibold mt-2" style={{ color: NAVY }}>{n}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Recent outcomes with explanations */}
      <section data-testid="og-recent">
        <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">
          Recent outcomes
        </div>
        <div className="space-y-2">
          {(data.recent_outcomes || []).length === 0 ? (
            <div className="text-[13px] text-slate-500">No outcomes yet.</div>
          ) : data.recent_outcomes.map((o) => {
            const tone = STATUS_TONE[o.status] || STATUS_TONE.acceptable;
            const isOpen = expanded[o.outcome_id];
            return (
              <div key={o.outcome_id} data-testid={`outcome-${o.outcome_id}`}
                   className="rounded-2xl bg-white border border-slate-100 shadow-sm">
                <button
                  onClick={() => setExpanded((e) => ({ ...e, [o.outcome_id]: !e[o.outcome_id] }))}
                  data-testid={`outcome-toggle-${o.outcome_id}`}
                  className="w-full flex items-start gap-3 p-4 text-left"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-[14px] capitalize" style={{ color: NAVY }}>
                        {o.outcome_type.replace(/_/g, " ")}
                      </div>
                      <span className="text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded"
                        style={{ background: tone.bg, color: tone.color }}>{tone.label}</span>
                      <span className="text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded" style={{ background: "#F1F5F9", color: "#475569" }}>
                        {o.industry.replace(/_/g, " ")}
                      </span>
                    </div>
                    <p className="text-[13px] text-slate-700 leading-relaxed mt-1">
                      {o.explanation}
                    </p>
                    <div className="text-[10px] text-slate-400 font-mono mt-2">
                      {formatDistanceToNow(new Date(o.generated_at), { addSuffix: true })}
                      {" · "}confidence {Math.round((o.confidence || 0) * 100)}%
                      {" · "}risk {o.risk_level}
                      {o.trust_request_id && ` · ${o.trust_request_id}`}
                    </div>
                  </div>
                </button>
                {isOpen && (
                  <div data-testid={`outcome-detail-${o.outcome_id}`} className="border-t border-slate-100 p-4 space-y-3">
                    {o.factors?.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Factors</div>
                        <ul className="text-[12px] text-slate-700 mt-1 space-y-0.5">
                          {o.factors.map((f, i) => (
                            <li key={i} className="font-mono">
                              {f.factor}{f.value != null && `: ${f.value}`}{f.threshold != null && ` (threshold ${f.threshold})`}
                              {f.satisfied != null && (f.satisfied ? " ✓" : " ✗")}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {o.recommended_actions?.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-widest font-mono text-amber-800">Recommended actions</div>
                        <ul className="text-[12px] text-slate-700 mt-1 space-y-0.5 list-disc list-inside">
                          {o.recommended_actions.map((a, i) => <li key={i}>{a}</li>)}
                        </ul>
                      </div>
                    )}
                    {o.evidence_links?.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Evidence links</div>
                        <ul className="text-[12px] text-slate-600 mt-1 space-y-0.5">
                          {o.evidence_links.map((l, i) => (
                            <li key={i} className="font-mono">
                              {l.kind}: <span className="text-slate-500">{l.reference_id}</span>
                              {l.label && ` — ${l.label}`}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {o.harm_indicators?.length > 0 && (
                      <div className="rounded-xl bg-red-50 border border-red-200 p-3 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-700 shrink-0 mt-0.5" />
                        <div className="text-[12px] text-red-900">
                          <strong>Harm indicators:</strong> {o.harm_indicators.join(", ")}
                        </div>
                      </div>
                    )}
                    <div className="text-[10px] text-slate-400 font-mono pt-2 border-t border-slate-100">
                      Rule: {o.rule_id} · v{o.rule_version}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <div className="text-[11px] text-slate-400 text-center pt-4 border-t border-slate-100">
        Continuous governance · Explainability first · Human oversight · Evidence before scoring
      </div>
    </div>
  );
}

function KpiTile({ icon: Icon, label, value, accent, emptyHint }) {
  const empty = value === null || value === undefined;
  return (
    <div data-testid={`og-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
         className="rounded-2xl bg-white border border-slate-100 shadow-sm p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
        <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{label}</span>
      </div>
      <div className="font-display text-2xl font-semibold" style={{ color: empty ? "#94A3B8" : accent }}>
        {empty ? "—" : value}
      </div>
      {empty && <div className="text-[10px] text-slate-400 mt-1">{emptyHint || "No data yet"}</div>}
    </div>
  );
}
